import { useEffect, useState } from "react";
import { useApp } from "@/context/AppContext";
import { projectsAPI } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Edit3, Trash2, Save, X, Sparkles, CheckCircle2, AlertTriangle, FolderOpen } from "lucide-react";

const TXT = {
    fr: {
        title: "Projets de chaîne",
        subtitle: "Un projet = un univers éditorial indépendant. Ta capacité totale est librement répartissable.",
        capacity: "Capacité totale",
        allocated: "Alloué",
        unallocated: "Non alloué",
        create_new: "Nouveau projet",
        active_badge: "ACTIF",
        demo_badge: "DÉMO",
        make_active: "Définir comme actif",
        name: "Nom",
        niche: "Niche",
        positioning: "Positionnement",
        character: "Personnage",
        allocation: "Capacité allouée",
        used_left: "utilisés · {left} restants",
        save: "Enregistrer",
        cancel: "Annuler",
        delete: "Supprimer",
        confirm_delete: "Supprimer ce projet ? La capacité sera libérée pour tes autres projets.",
        realloc_hint: "Ajuste la capacité de chaque projet. La somme ne peut pas dépasser ta capacité totale.",
        realloc_cta: "Appliquer la répartition",
        no_capacity: "Aucune capacité active. Achète OneShot ou Creator pour créer des projets réels.",
        demo_note: "Ce projet démo utilise une capacité virtuelle de 5 Shorts qui ne touche pas ton quota payant.",
    },
    en: {
        title: "Channel projects",
        subtitle: "A project = one independent editorial universe. Your total capacity is freely allocatable.",
        capacity: "Total capacity",
        allocated: "Allocated",
        unallocated: "Unallocated",
        create_new: "New project",
        active_badge: "ACTIVE",
        demo_badge: "DEMO",
        make_active: "Set as active",
        name: "Name",
        niche: "Niche",
        positioning: "Positioning",
        character: "Character",
        allocation: "Allocated capacity",
        used_left: "used · {left} remaining",
        save: "Save",
        cancel: "Cancel",
        delete: "Delete",
        confirm_delete: "Delete this project? Its capacity will be freed up for other projects.",
        realloc_hint: "Adjust each project's capacity. Sum can't exceed your total capacity.",
        realloc_cta: "Apply allocation",
        no_capacity: "No active capacity. Buy OneShot or Creator to create real projects.",
        demo_note: "This demo project uses a virtual capacity of 5 Shorts that doesn't touch your paid quota.",
    },
    es: {
        title: "Proyectos de canal",
        subtitle: "Un proyecto = un universo editorial independiente. Tu capacidad total se puede repartir libremente.",
        capacity: "Capacidad total",
        allocated: "Asignado",
        unallocated: "Sin asignar",
        create_new: "Nuevo proyecto",
        active_badge: "ACTIVO",
        demo_badge: "DEMO",
        make_active: "Marcar como activo",
        name: "Nombre",
        niche: "Nicho",
        positioning: "Posicionamiento",
        character: "Personaje",
        allocation: "Capacidad asignada",
        used_left: "usados · {left} restantes",
        save: "Guardar",
        cancel: "Cancelar",
        delete: "Eliminar",
        confirm_delete: "¿Eliminar este proyecto? Su capacidad quedará libre para otros proyectos.",
        realloc_hint: "Ajusta la capacidad de cada proyecto. La suma no puede superar tu capacidad total.",
        realloc_cta: "Aplicar reparto",
        no_capacity: "Sin capacidad activa. Compra OneShot o Creator para crear proyectos reales.",
        demo_note: "Este proyecto demo usa una capacidad virtual de 5 Shorts que no toca tu cuota pagada.",
    },
};

export default function Projects() {
    const { lang, projects, projectsSummary, refreshProjects, activeProjectId, setActiveProjectId } = useApp();
    const t = TXT[lang] || TXT.fr;
    const [creating, setCreating] = useState(false);
    const [editing, setEditing] = useState(null); // project object being edited
    const [form, setForm] = useState({ name: "", niche: "", positioning: "", character: "", quota_allocated: 0 });
    const [drafts, setDrafts] = useState({}); // pid -> quota_allocated draft

    useEffect(() => { refreshProjects(); }, [refreshProjects]);

    const startCreate = () => { setForm({ name: "", niche: "", positioning: "", character: "", quota_allocated: 0 }); setCreating(true); };
    const startEdit = (p) => { setForm({ name: p.name, niche: p.niche || "", positioning: p.positioning || "", character: p.character || "", quota_allocated: p.quota_allocated }); setEditing(p); };
    const cancelForm = () => { setCreating(false); setEditing(null); };

    const submit = async () => {
        try {
            if (editing) {
                await projectsAPI.update(editing.id, { name: form.name, niche: form.niche, positioning: form.positioning, character: form.character });
            } else {
                await projectsAPI.create(form);
            }
            toast.success(t.save + " ✓");
            cancelForm();
            await refreshProjects();
        } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    };

    const removeProject = async (p) => {
        if (!window.confirm(t.confirm_delete)) return;
        try {
            await projectsAPI.remove(p.id);
            toast.success(t.delete + " ✓");
            await refreshProjects();
        } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    };

    const applyReallocation = async () => {
        const allocations = {};
        for (const p of projects) {
            if (p.is_demo) continue;
            allocations[p.id] = drafts[p.id] ?? p.quota_allocated;
        }
        try {
            await projectsAPI.allocate(allocations);
            toast.success(t.realloc_cta + " ✓");
            setDrafts({});
            await refreshProjects();
        } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    };

    const draftSum = projects.filter((p) => !p.is_demo).reduce((s, p) => s + (drafts[p.id] ?? p.quota_allocated), 0);
    const overflow = draftSum > projectsSummary.capacity;
    const changed = Object.keys(drafts).length > 0;

    return (
        <div data-testid="projects-module" className="space-y-6">
            {/* Global capacity strip */}
            <div className="rounded-lg border border-[#253046] bg-gradient-to-r from-[#0F141C] via-[#0B111B] to-[#0F141C] p-4 sm:p-5">
                <div className="flex flex-wrap items-baseline gap-6">
                    <div>
                        <div className="text-[10px] font-mono-sf uppercase tracking-widest text-slate-500">{t.capacity}</div>
                        <div data-testid="projects-capacity" className="font-display text-3xl font-black text-white leading-none">{projectsSummary.capacity}</div>
                    </div>
                    <div>
                        <div className="text-[10px] font-mono-sf uppercase tracking-widest text-slate-500">{t.allocated}</div>
                        <div data-testid="projects-allocated" className="font-display text-3xl font-black flame-text leading-none">{projectsSummary.allocated}</div>
                    </div>
                    <div>
                        <div className="text-[10px] font-mono-sf uppercase tracking-widest text-slate-500">{t.unallocated}</div>
                        <div data-testid="projects-unallocated" className="font-display text-3xl font-black text-[#10B981] leading-none">{projectsSummary.unallocated}</div>
                    </div>
                    <button
                        data-testid="projects-create-btn"
                        onClick={startCreate}
                        disabled={projectsSummary.capacity === 0}
                        className="ml-auto flame-gradient text-black font-semibold px-4 py-2.5 rounded-md text-sm kinetic flex items-center gap-2 disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                        <Plus className="w-4 h-4" /> {t.create_new}
                    </button>
                </div>
                <div className="mt-2 text-xs text-slate-400">{t.subtitle}</div>
                {projectsSummary.capacity === 0 && <div className="mt-3 text-xs text-[#FFB800] flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5" /> {t.no_capacity}</div>}
            </div>

            {/* Create / Edit form */}
            {(creating || editing) && (
                <div data-testid="projects-form" className="rounded-lg border-2 border-[#FF4D00]/50 bg-[#0F141C] p-5 space-y-3">
                    <div className="grid sm:grid-cols-2 gap-3">
                        <label className="block">
                            <div className="text-[10px] font-mono-sf uppercase tracking-widest text-slate-500 mb-1">{t.name}</div>
                            <input data-testid="proj-name-input" className="w-full bg-[#0B111B] border border-[#253046] rounded px-3 py-2 text-white" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                        </label>
                        <label className="block">
                            <div className="text-[10px] font-mono-sf uppercase tracking-widest text-slate-500 mb-1">{t.niche}</div>
                            <input className="w-full bg-[#0B111B] border border-[#253046] rounded px-3 py-2 text-white" value={form.niche} onChange={(e) => setForm({ ...form, niche: e.target.value })} />
                        </label>
                        <label className="block sm:col-span-2">
                            <div className="text-[10px] font-mono-sf uppercase tracking-widest text-slate-500 mb-1">{t.positioning}</div>
                            <textarea rows={2} className="w-full bg-[#0B111B] border border-[#253046] rounded px-3 py-2 text-white text-sm" value={form.positioning} onChange={(e) => setForm({ ...form, positioning: e.target.value })} />
                        </label>
                        <label className="block sm:col-span-2">
                            <div className="text-[10px] font-mono-sf uppercase tracking-widest text-slate-500 mb-1">{t.character}</div>
                            <textarea rows={2} className="w-full bg-[#0B111B] border border-[#253046] rounded px-3 py-2 text-white text-sm" value={form.character} onChange={(e) => setForm({ ...form, character: e.target.value })} />
                        </label>
                        {creating && (
                            <label className="block">
                                <div className="text-[10px] font-mono-sf uppercase tracking-widest text-slate-500 mb-1">{t.allocation} ({projectsSummary.unallocated} {t.unallocated.toLowerCase()})</div>
                                <input data-testid="proj-alloc-input" type="number" min="0" max={projectsSummary.unallocated} className="w-full bg-[#0B111B] border border-[#253046] rounded px-3 py-2 text-white" value={form.quota_allocated} onChange={(e) => setForm({ ...form, quota_allocated: Number(e.target.value) || 0 })} />
                            </label>
                        )}
                    </div>
                    <div className="flex justify-end gap-2">
                        <button onClick={cancelForm} className="px-4 py-2 rounded border border-[#253046] text-slate-300 hover:text-white text-sm flex items-center gap-1.5"><X className="w-3.5 h-3.5" /> {t.cancel}</button>
                        <button data-testid="proj-save-btn" onClick={submit} disabled={!form.name.trim()} className="flame-gradient text-black font-semibold px-4 py-2 rounded text-sm flex items-center gap-1.5 disabled:opacity-30"><Save className="w-3.5 h-3.5" /> {t.save}</button>
                    </div>
                </div>
            )}

            {/* Projects grid */}
            <div className="grid md:grid-cols-2 gap-4">
                {projects.map((p) => {
                    const draftVal = drafts[p.id] ?? p.quota_allocated;
                    const used = p.quota_used || 0;
                    const left = Math.max(0, (p.is_demo ? p.quota_allocated : draftVal) - used);
                    const pct = draftVal > 0 ? Math.min(100, Math.round((used / draftVal) * 100)) : 0;
                    const isActive = activeProjectId === p.id;
                    return (
                        <div key={p.id} data-testid={`project-card-${p.id}`} className={`rounded-lg border ${isActive ? "border-[#FF4D00]" : "border-[#253046]"} bg-[#0B111B] p-5 kinetic`}>
                            <div className="flex items-start justify-between gap-2 mb-2">
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-1">
                                        <FolderOpen className={`w-4 h-4 ${isActive ? "text-[#FF4D00]" : "text-slate-500"}`} />
                                        {p.is_demo && <span className="text-[9px] font-mono-sf tracking-widest px-1.5 py-0.5 rounded bg-[#FFB800]/15 text-[#FFB800]">{t.demo_badge}</span>}
                                        {isActive && <span data-testid={`project-active-${p.id}`} className="text-[9px] font-mono-sf tracking-widest px-1.5 py-0.5 rounded bg-[#FF4D00]/15 text-[#FF4D00]">{t.active_badge}</span>}
                                    </div>
                                    <div className="font-display text-lg font-bold uppercase leading-tight">{p.name}</div>
                                    {p.niche && <div className="text-xs text-slate-500 mt-0.5">{p.niche}</div>}
                                </div>
                                <div className="flex gap-1">
                                    {!p.is_demo && (
                                        <button data-testid={`project-edit-${p.id}`} onClick={() => startEdit(p)} className="text-slate-400 hover:text-white p-1"><Edit3 className="w-4 h-4" /></button>
                                    )}
                                    {!p.is_demo && (
                                        <button data-testid={`project-delete-${p.id}`} onClick={() => removeProject(p)} className="text-slate-400 hover:text-red-400 p-1"><Trash2 className="w-4 h-4" /></button>
                                    )}
                                </div>
                            </div>

                            {p.positioning && <p className="text-xs text-slate-400 italic mt-1 mb-3 line-clamp-2">{p.positioning}</p>}

                            {/* Quota strip */}
                            <div className="mt-3">
                                <div className="flex items-baseline justify-between mb-1">
                                    {p.is_demo ? (
                                        <span className="text-[11px] font-mono-sf text-slate-500 uppercase tracking-widest">{used} / {p.quota_allocated}</span>
                                    ) : (
                                        <>
                                            <input
                                                data-testid={`project-alloc-${p.id}`}
                                                type="number"
                                                min={used}
                                                max={draftVal + projectsSummary.unallocated}
                                                value={draftVal}
                                                onChange={(e) => setDrafts({ ...drafts, [p.id]: Math.max(used, Number(e.target.value) || 0) })}
                                                className="w-16 bg-[#0F141C] border border-[#253046] rounded px-2 py-0.5 text-white font-display text-sm text-center"
                                            />
                                            <span className="text-[10px] font-mono-sf text-slate-500">{used} {t.used_left.replace("{left}", left)}</span>
                                        </>
                                    )}
                                </div>
                                <div className="h-1.5 rounded-full bg-[#161D2A] overflow-hidden">
                                    <div className={`h-full ${pct >= 100 ? "bg-red-500" : pct >= 80 ? "bg-[#FFB800]" : "bg-[#FF4D00]"} transition-all duration-500`} style={{ width: `${pct}%` }} />
                                </div>
                            </div>

                            <div className="mt-4 flex items-center gap-2">
                                {!isActive && (
                                    <button data-testid={`project-activate-${p.id}`} onClick={() => setActiveProjectId(p.id)} className="text-xs px-3 py-1.5 rounded border border-[#253046] hover:border-[#FF4D00] text-slate-300 hover:text-white kinetic flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5" /> {t.make_active}</button>
                                )}
                                {p.is_demo && <span className="text-[10px] italic text-slate-500">{t.demo_note}</span>}
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Reallocation footer */}
            {changed && (
                <div data-testid="projects-realloc-bar" className={`sticky bottom-4 rounded-lg border-2 ${overflow ? "border-red-500/60" : "border-[#FF4D00]/60"} bg-[#0F141C] p-4 flex flex-wrap items-center justify-between gap-3`}>
                    <div className="text-sm">
                        <div className="font-display uppercase font-bold">{t.realloc_hint}</div>
                        <div className={`text-xs mt-1 font-mono-sf ${overflow ? "text-red-400" : "text-slate-400"}`}>
                            {draftSum} / {projectsSummary.capacity}
                        </div>
                    </div>
                    <div className="flex gap-2">
                        <button onClick={() => setDrafts({})} className="px-3 py-2 rounded border border-[#253046] text-slate-300 hover:text-white text-sm">{t.cancel}</button>
                        <button data-testid="projects-realloc-apply" onClick={applyReallocation} disabled={overflow} className="flame-gradient text-black font-semibold px-4 py-2 rounded text-sm flex items-center gap-1.5 disabled:opacity-30"><Sparkles className="w-3.5 h-3.5" /> {t.realloc_cta}</button>
                    </div>
                </div>
            )}
        </div>
    );
}
